import { normalizeDesktopColumnLayout } from 'src/helpers/columnDistribution';
import {
  BreakpointRowConfigValue,
  DesktopRowConfigValue,
  decodeBreakpointRowConfig,
  encodeBreakpointRowConfig,
} from 'src/helpers/columnsLayoutUtils';
import { IColumnsLayout } from 'src/interfaces/PageColumn';

export function desktopColumnCountOptions(): (2 | 3 | 4)[] {
  return [2, 3, 4];
}

export function desktopConfigOptions(): DesktopRowConfigValue[] {
  return ['2', '3', '3-wide', '4'];
}

export function breakpointConfigOptions(columnCount: number): BreakpointRowConfigValue[] {
  const options: BreakpointRowConfigValue[] = [
    encodeBreakpointRowConfig(1, false),
    encodeBreakpointRowConfig(1, true),
  ];
  for (let k = 2; k <= columnCount; k += 1) {
    options.push(encodeBreakpointRowConfig(k, false));
    options.push(encodeBreakpointRowConfig(k, true));
  }
  return options;
}

export function applyDesktopColumnCount(
  layout: IColumnsLayout,
  columnCount: 2 | 3 | 4,
): IColumnsLayout {
  const n = layout.columns.length;
  return {
    ...layout,
    columnsPerRow: { ...layout.columnsPerRow!, md: Math.min(columnCount, n) },
    desktopColumnLayout: normalizeDesktopColumnLayout(n, layout.desktopColumnLayout),
    columnOrder: {
      ...layout.columnOrder,
      md: layout.columnOrder?.md ?? Array.from({ length: n }, (_, i) => i),
    },
  };
}

export function applyBreakpointConfig(
  layout: IColumnsLayout,
  bp: 'xs' | 'sm',
  value: BreakpointRowConfigValue,
): IColumnsLayout {
  const { perRow, order } = decodeBreakpointRowConfig(value, layout.columns.length);
  const key = bp === 'xs' ? 'xs' : 'sm';
  return {
    ...layout,
    columnsPerRow: { ...layout.columnsPerRow!, [key]: perRow },
    columnOrder: { ...layout.columnOrder, [bp]: order },
    mobileColumnOrder: bp === 'xs' ? order : layout.mobileColumnOrder,
  };
}

export function desktopOptionLabel(
  value: DesktopRowConfigValue,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (value === '3-wide') {
    return t('system.pages.columnsDesktopWideCenter');
  }
  return t('system.pages.columnsDesktopCount', { count: Number(value) });
}

export function breakpointOptionLabel(
  value: BreakpointRowConfigValue,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const reversed = value.endsWith('-reversed');
  const count = Number.parseInt(value, 10);
  if (count === 1) {
    return reversed
      ? t('system.pages.columnsPerRowCountReversed', { count: 1 })
      : t('system.pages.columnsPerRowOne');
  }
  if (reversed) {
    return t('system.pages.columnsPerRowCountReversed', { count });
  }
  return t('system.pages.columnsPerRowCount', { count });
}
